import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import cogoToast from "cogo-toast";

import { SectionLoader } from "../../../../../components/PageLoader";
import { uploadFile } from "../../../../../utils/helpers";

import {
  useCommunityPosts,
  usePostComments,
  useCreatePost,
  useDeletePost,
  useToggleLike,
  useCreateComment,
  useDeleteComment,
} from "../../../../../hooks/queries/communityQueries";

import ShareButton from "../ShareButton";
import "../feedTabs.css";
import "./communityTab.css";

/**
 * CommunityTab — the user-generated social feed (modern social design).
 *
 * Now powered by TanStack Query for caching, optimistic updates, and
 * automatic refetch. Redux is only used for auth (userData).
 *
 * Contains:
 *   - Post composer (text + image upload with preview)
 *   - Post list with author avatar, timestamp, content, image grid
 *   - Engagement bar (like, comment, share)
 *   - Comments (expand/collapse, lazy-loaded, add, delete)
 *   - Delete own post
 */
const CommunityTab = () => {
  const FEED_PAGE = 1;
  const { userData } = useSelector((store) => store.auth);

  // ── TanStack Query: data fetching ──
  const { data: feedsArray = [], isLoading: loading } = useCommunityPosts(FEED_PAGE);

  // ── TanStack Query: mutations ──
  const createPostMutation = useCreatePost(FEED_PAGE);
  const deletePostMutation = useDeletePost(FEED_PAGE);
  const toggleLikeMutation = useToggleLike(FEED_PAGE);
  const createCommentMutation = useCreateComment(FEED_PAGE);
  const deleteCommentMutation = useDeleteComment(FEED_PAGE);

  // ── Local UI state ──
  const uploadInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [postContent, setPostContent] = useState("");
  const [postImage, setPostImage] = useState("");
  const [commentText, setCommentText] = useState("");

  // ── Comments query (enabled only when panel is expanded) ──
  const expandedPost = feedsArray.find((p) => String(p.id) === String(expandedPostId));
  const { data: expandedComments, isLoading: commentsLoading } = usePostComments(
    expandedPostId,
    Boolean(expandedPostId)
  );

  // Use fetched comments if available, otherwise fall back to comments
  // embedded in the post object (from the list endpoint).
  const commentsForExpanded = expandedComments ?? expandedPost?.comments ?? [];

  // ── Image upload ──
  const handleChangeUpload = ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);

      setIsUploading(true);
      Promise.resolve(
        uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        )
      )
        .then(({ data, success }) => {
          if (success) {
            setPostImage(data);
          } else {
            cogoToast.error("Unsuccessful image upload", {
              hideAfter: 4,
              position: "top-center",
            });
            setImagePreview("");
          }
        })
        .finally(() => {
          setIsUploading(false);
        });
    }
  };

  const removeImage = () => {
    setPostImage("");
    setImagePreview("");
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  // ── Submit post ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postContent.trim()) {
      cogoToast.error("Please enter some content for your post", {
        hideAfter: 3,
        position: "top-center",
      });
      return;
    }

    try {
      await createPostMutation.mutateAsync({
        body: postContent,
        image_urls: postImage ? [postImage] : [],
      });
      setPostContent("");
      setPostImage("");
      setImagePreview("");
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    } catch (error) {
      cogoToast.error(error.message || "Failed to create post", {
        hideAfter: 4,
        position: "top-center",
      });
    }
  };

  // ── Comments ──
  const handleSubmitComment = async (e, feedId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!commentText.trim()) {
      cogoToast.error("Please enter a comment", {
        hideAfter: 3,
        position: "top-center",
      });
      return;
    }

    try {
      await createCommentMutation.mutateAsync({
        postId: feedId,
        body: commentText,
      });
      setCommentText("");
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const deleteFeedSubmit = async (postId) => {
    try {
      await deletePostMutation.mutateAsync(postId);
    } catch (error) {
      console.error("Error deleting feed:", error);
    }
  };

  const deleteCommentSubmit = async (commentId, feedId) => {
    try {
      await deleteCommentMutation.mutateAsync({
        postId: feedId,
        commentId,
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleToggleLike = async (feedId, likedByMe) => {
    try {
      await toggleLikeMutation.mutateAsync({ postId: feedId, likedByMe });
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  const handleToggleComments = (postId) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const myAvatar =
    userData?.avatar ||
    "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png";
  const myName =
    userData?.first_name || userData?.last_name
      ? `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim()
      : "You";

  const isPosting = createPostMutation.isPending;

  return (
    <div className="community-tab">
      {/* ── Post Composer ── */}
      <div className="community-composer">
        <div className="community-composer-header">
          <img src={myAvatar} alt="" className="community-avatar-sm" />
          <span className="community-composer-prompt">Share something with the community...</span>
        </div>

        <form onSubmit={handleSubmit}>
          <textarea
            className="community-composer-textarea"
            placeholder="What's on your mind? Ask a question, share a win, or start a conversation..."
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            rows={3}
          />

          {/* Image preview */}
          {imagePreview && (
            <div className="community-composer-preview">
              <img src={imagePreview} alt="Preview" />
              <button type="button" onClick={removeImage} className="community-preview-remove">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              {isUploading && (
                <div className="community-preview-uploading">
                  <div className="community-spinner" />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          )}

          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            onChange={handleChangeUpload}
            style={{ display: "none" }}
          />

          <div className="community-composer-actions">
            <button
              type="button"
              className="community-composer-attach"
              onClick={() => uploadInputRef.current?.click()}
              disabled={isUploading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>Photo</span>
            </button>

            <button
              type="submit"
              className="community-composer-post"
              disabled={isPosting || isUploading || !postContent.trim()}
            >
              {isPosting ? (
                <>
                  <div className="community-spinner community-spinner-sm" />
                  Posting...
                </>
              ) : (
                "Post"
              )}
            </button>
          </div>
        </form>
      </div>

      {loading && feedsArray.length === 0 && <SectionLoader />}

      {/* ── Empty state ── */}
      {!loading && feedsArray.length === 0 && (
        <div className="community-empty">
          <div className="community-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3>Be the first to share something</h3>
          <p>Ask a question, celebrate a win, or start a conversation.</p>
        </div>
      )}

      {/* ── Post List ── */}
      <div className="community-posts">
        {feedsArray.map((item, index) => {
          const images = Array.isArray(item.image_urls) ? item.image_urls : (item.img_url ? [item.img_url] : []);
          const isOwnPost = userData?.id && item.author_id == userData.id;
          const isExpanded = expandedPostId === item.id;
          const itemComments = isExpanded ? commentsForExpanded : item.comments;

          return (
            <div className="community-post-card" key={item.id || index}>
              {/* Post header */}
              <div className="community-post-header">
                <img
                  src={item.author_avatar || "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png"}
                  alt=""
                  className="community-avatar-md"
                />
                <div className="community-post-meta">
                  <span className="community-post-author">{item.author_name || "User"}</span>
                  <span className="community-post-time">{item.created_at}</span>
                </div>
                {isOwnPost && (
                  <button
                    type="button"
                    onClick={() => deleteFeedSubmit(item.id)}
                    disabled={deletePostMutation.isPending && deletePostMutation.variables === item.id}
                    className="community-post-delete"
                    title="Delete post"
                  >
                    {deletePostMutation.isPending && deletePostMutation.variables === item.id ? (
                      <div className="community-spinner community-spinner-sm" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              {/* Post body */}
              {item.body && <p className="community-post-body">{item.body}</p>}

              {/* Post images */}
              {images.length > 0 && (
                <div className={`community-post-images community-post-images-${Math.min(images.length, 4)}`}>
                  {images.slice(0, 4).map((url, imgIdx) => (
                    <img key={imgIdx} src={url} alt="" className="community-post-image" />
                  ))}
                </div>
              )}

              {/* Engagement bar */}
              <div className="community-engagement-bar">
                <button
                  type="button"
                  onClick={() => handleToggleLike(item.id, item.liked_by_me)}
                  disabled={toggleLikeMutation.isPending && toggleLikeMutation.variables?.postId === item.id}
                  className={`community-engagement-btn ${item.liked_by_me ? "community-liked" : ""}`}
                >
                  <svg
                    width="20" height="20" viewBox="0 0 24 24"
                    fill={item.liked_by_me ? "currentColor" : "none"}
                    stroke="currentColor" strokeWidth="2"
                    className={item.liked_by_me ? "community-heart-pop" : ""}
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  <span>{item.like_count > 0 ? item.like_count : "Like"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleToggleComments(item.id)}
                  className={`community-engagement-btn ${isExpanded ? "community-engagement-active" : ""}`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>{item.comment_count > 0 ? item.comment_count : "Comment"}</span>
                </button>

                <ShareButton
                  url={`${window.location.origin}/community?post=${item.id}`}
                  title="workbrook Community Post"
                  text={item.body ? item.body.substring(0, 100) : "Check out this post on workbrook"}
                  className="community-engagement-btn"
                />
              </div>

              {/* Comments panel */}
              {isExpanded && (
                <div className="community-comments-panel">
                  {/* Comment input */}
                  <form
                    onSubmit={(e) => handleSubmitComment(e, item.id)}
                    className="community-comment-form"
                  >
                    <img src={myAvatar} alt="" className="community-avatar-xs" />
                    <input
                      type="text"
                      onChange={({ target: { value } }) => setCommentText(value)}
                      value={isExpanded ? commentText : ""}
                      placeholder="Write a comment..."
                      disabled={createCommentMutation.isPending}
                      className="community-comment-input"
                    />
                    <button
                      type="submit"
                      disabled={createCommentMutation.isPending || !commentText.trim()}
                      className="community-comment-submit"
                    >
                      {createCommentMutation.isPending ? (
                        <div className="community-spinner community-spinner-sm" />
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                      )}
                    </button>
                  </form>

                  {/* Comments list */}
                  {commentsLoading && (
                    <div className="community-comments-loading">Loading comments...</div>
                  )}

                  {!commentsLoading && itemComments && itemComments.length > 0 && (
                    <div className="community-comments-list">
                      {itemComments.map((comment, commentIndex) => (
                        <div key={comment.id || commentIndex} className="community-comment">
                          <img
                            src={comment.avatar || "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png"}
                            alt=""
                            className="community-avatar-xs"
                          />
                          <div className="community-comment-body">
                            <div className="community-comment-bubble">
                              <span className="community-comment-author">{comment.author_name || "User"}</span>
                              <p className="community-comment-text">{comment.body}</p>
                            </div>
                            {userData?.id && comment.user_id == userData.id && (
                              <button
                                type="button"
                                onClick={() => deleteCommentSubmit(comment.id, item.id)}
                                disabled={deleteCommentMutation.isPending &&
                                  deleteCommentMutation.variables?.commentId === comment.id}
                                className="community-comment-delete"
                              >
                                {deleteCommentMutation.isPending &&
                                  deleteCommentMutation.variables?.commentId === comment.id ? "..." : "Delete"}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!commentsLoading && (!itemComments || itemComments.length === 0) && (
                    <div className="community-comments-empty">No comments yet. Be the first to comment!</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommunityTab;
